import React, { useState, useEffect } from 'react';
import Box from '@material-ui/core/Box';
import Drawer from '@material-ui/core/Drawer';
import Button from '@material-ui/core/Button';
import Typography from '@material-ui/core/Typography';
import CircularProgress from '@material-ui/core/CircularProgress';
import { getLatestDocumentResult, updateFeedback } from 'src/api/documents';
import { useSelector } from 'react-redux';

export default function SuggestedAnswersDrawer({ open, onClose }) {
  const [loading, setLoading] = useState(true);
  const [answers, setAnswers] = useState([]);
  const [message, setMessage] = useState(null); // Used for errors or info messages
  const [processingId, setProcessingId] = useState(null);
  const { currentRole } = useSelector(({ auth }) => auth);

  useEffect(() => {
    // Auto-popup logic: Check if user is FOUNDER and has document results
    const checkAndAutoOpen = async () => {
      if (currentRole === 'FOUNDER') {
        try {
          const result = await getLatestDocumentResult();
          if (result && result.answers && result.answers.length > 0) {
            const highConfidenceAnswers = result.answers.filter(
              (ans) => ans.confidence >= 0.7
            );
            
            if (highConfidenceAnswers.length > 0) {
                          console.log('🔵 Auto-popup: Setting answers and processingId:', { 
              answersCount: highConfidenceAnswers.length, 
              processingId: result.id,
              fullResult: result 
            });
              // Auto-open drawer with results
              setAnswers(highConfidenceAnswers);
              setProcessingId(result.id || null);
              setLoading(false);
              // Note: We can't auto-open the drawer from here, parent needs to handle this
              // This will be handled by the parent component
            }
          }
        } catch (err) {
          console.log('No document results found for auto-popup');
        }
      }
    };

    // Check for auto-popup on component mount
    checkAndAutoOpen();
  }, [currentRole]);

  useEffect(() => {
    if (open) {
      const fetchLatestResult = async () => {
        setLoading(true);
        setMessage(null);
        setAnswers([]); // Reset state on each open
        try {
          const result = await getLatestDocumentResult();
          const highConfidenceAnswers = result.answers.filter(
            (ans) => ans.confidence >= 0.2
          );

          if (highConfidenceAnswers.length === 0) {
            setMessage('The pitch deck did not contain answers to the questions.');
          } else {
            console.log('🔵 Setting answers and processingId:', { 
              answersCount: highConfidenceAnswers.length, 
              processingId: result.id,
              fullResult: result 
            });
            setAnswers(highConfidenceAnswers);
            setProcessingId(result.id || null);
          }
        } catch (err) {
          if (err.response?.status === 400) {
            // Display the exact message from the backend
            setMessage(err.response?.data?.message || 'An error occurred, but no message was provided.');
          } else {
            setMessage(err);
          }
        } finally {
          setLoading(false);
        }
      };

      fetchLatestResult();
    }
  }, [open]);

  const handleAccept = async (answerId) => {
    console.log('🔵 handleAccept called with:', { answerId, processingId });
    
    if (!processingId) {
      console.log('🔴 processingId is null/undefined - cannot proceed');
      return;
    }
    
    try {
      console.log('🔵 Calling updateFeedback API with:', { processingId, answerId, feedbackType: 'accept' });
      const success = await updateFeedback(processingId, answerId, 'accept');
      console.log('🔵 updateFeedback API response:', success);
      
      if (success) {
        console.log('🔵 Updating local state for answer:', answerId);
        setAnswers(prev => prev.map(ans => 
          ans.id === answerId 
            ? { ...ans, feedbackStatus: 'accept' }
            : ans
        ));
        console.log('🔵 Local state updated successfully');
      } else {
        console.log('🔴 updateFeedback returned false');
      }
    } catch (error) {
      console.error('🔴 Error accepting answer:', error);
    }
  };

  const handleReject = async (answerId) => {
    if (!processingId) return;
    
    try {
      await updateFeedback(processingId, answerId, 'reject');
      // Update local state to show rejected status
      setAnswers(prev => prev.map(ans => 
        ans.id === answerId 
          ? { ...ans, feedbackStatus: 'reject' }
          : ans
      ));
    } catch (error) {
      console.error('Failed to save feedback:', error);
    }
  };

  const renderContent = () => {
    if (loading) {
      return (
        <Box display="flex" justifyContent="center" alignItems="center" height="100%">
          <CircularProgress />
        </Box>
      );
    }

    if (message) {
        return (
          <Box p={3} display="flex" justifyContent="center" alignItems="center" height="100%">
            <Typography style={{ color: 'blue' }}>{message}</Typography>
          </Box>
        );
      }
    
    if (answers.length > 0) {
      return (
        <Box p={3}>
          <Typography variant="h6" gutterBottom>Derived answers from your pitch deck</Typography>
          {answers.map((item) => {
            const hasFeedback = item.feedbackStatus;
            const isAccepted = item.feedbackStatus === 'accept';
            const isRejected = item.feedbackStatus === 'reject';
            
            return (
              <Box 
                key={item.id} 
                mb={2} 
                p={2} 
                border={1} 
                borderRadius={4} 
                borderColor={hasFeedback ? 'grey.400' : 'grey.300'}
                style={{ 
                  opacity: hasFeedback ? 0.6 : 1,
                  backgroundColor: hasFeedback ? '#f5f5f5' : 'white',
                  position: 'relative'
                }}
              >
                {/* Status Badge */}
                {hasFeedback && (
                  <Box 
                    position="absolute" 
                    top={-10} 
                    left={10} 
                    bgcolor={isAccepted ? 'green' : 'red'}
                    color="white" 
                    px={1} 
                    py={0.5} 
                    borderRadius={1}
                    fontSize={12}
                    fontWeight="bold"
                    zIndex={1}
                  >
                    {isAccepted ? 'ACCEPTED' : 'REJECTED'}
                  </Box>
                )}
                
                <Typography variant="subtitle1" gutterBottom>{item.question}</Typography>
                <Typography variant="body1" color="textSecondary" gutterBottom>
                  <strong>Suggested Answer:</strong> {item.answer}
                </Typography>
                
                {/* Buttons - Only show if no feedback given */}
                {!hasFeedback && (
                  <Box display="flex" justifyContent="flex-end" mt={1}>
                    <Button size="small" color="primary" onClick={() => handleAccept(item.id)}>
                      Accept
                    </Button>
                    <Button size="small" color="secondary" onClick={() => handleReject(item.id)}>
                      Reject
                    </Button>
                  </Box>
                )}
              </Box>
            );
          })}
        </Box>
      );
    }

    return null; // Fallback, should not be reached
  };

  return (
    <Drawer anchor="right" open={open} onClose={onClose}>
      <Box sx={{ width: 450 }} role="presentation">
        {renderContent()}
      </Box>
    </Drawer>
  );
}